import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type {
  Schedule,
  ScheduleTag,
  ScheduleType,
} from '@data/fms/schedule/types';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export type PostScheduleRegisterBodyTask = {
  task_type: 'move';
  destination_point?: number;
  means?: 'manual' | 'auto';
  duration_sec?: number;
  description?: string;
  partial_route_ids?: string[];
  back_home?: boolean;
};

export type PostScheduleRegisterBody = {
  area_map_version_id?: string;
  schedule_type?: ScheduleType;
  expiration_date?: string;
  tasks: PostScheduleRegisterBodyTask[];
  tags?: ScheduleTag[];
};

/**
 * スケジュール登録API
 */
export const useScheduleRegisterAPI = (): {
  state: APIState;
  postScheduleRegister: (
    vehicleId: string,
    body: PostScheduleRegisterBody,
  ) => Promise<Schedule | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const postScheduleRegisterAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      body: PostScheduleRegisterBody,
    ): Promise<Result<Schedule, AxiosResponse>> => {
      try {
        const res = await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/schedules`,
          body,
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postScheduleRegister = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, body: PostScheduleRegisterBody) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t('api.fms.post_schedule_register', { status: 'failed' }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');

        // タグに app_name を追加
        if (!!body.tags && Array.isArray(body.tags)) {
          body = {
            ...body,
            tags: [...body.tags, { key: 'app_name', value: 'FMS Console' }],
          };
        } else {
          body = {
            ...body,
            tags: [{ key: 'app_name', value: 'FMS Console' }],
          };
        }

        const res = await postScheduleRegisterAPI(
          project!.id,
          environment!.environment_id,
          vehicleId,
          body,
        );
        if (!res.value || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_schedule_register', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        notifySuccess({
          message: t('api.fms.post_schedule_register', {
            status: 'successfully',
          }),
          testIdAttrs: {
            wrapperTestId: 'createSchedule-snackbar',
            closeButtonTestId: 'createSchedule-snackbar-close-btn',
          },
        });
        setState('hasValue');
        return res.value;
      },
      [notifySuccess, notifyError, postScheduleRegisterAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    postScheduleRegister,
  };
};
